import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { cargoMediaSrc } from '@/lib/bubbleImage';
import { ChatCircleText, Microphone, Stop, PaperPlaneTilt } from '@phosphor-icons/react';
import FounderBadge from './FounderBadge';

/**
 * Threaded comments under a Scene. Text + 20s voice messages.
 * Reuses cargoMediaSrc to resolve voice URLs.
 */
const SceneComments = ({ scene }) => {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [bubbles, setBubbles] = useState([]);
  const [bubbleId, setBubbleId] = useState('');
  const [busy, setBusy] = useState(false);
  const [audioB64, setAudioB64] = useState(null);
  const [recording, setRecording] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const load = async () => {
    try {
      const { data } = await api.get(`/scenes/${scene.id}/comments`);
      setComments(data.comments || []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    load();
    api.get('/bubbles/mine').then(({ data }) => {
      setBubbles(data.bubbles || []);
      if (data.bubbles?.[0]) setBubbleId(data.bubbles[0].id);
    });
  }, [scene.id]);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const b64 = await new Promise((res) => {
          const r = new FileReader(); r.onload = () => res(String(r.result || '').split(',')[1] || ''); r.readAsDataURL(blob);
        });
        setAudioB64(b64);
        stream.getTracks().forEach((t) => t.stop());
      };
      rec.start(); recRef.current = rec; setRecording(true);
      setTimeout(() => { if (rec.state === 'recording') rec.stop(); setRecording(false); }, 20000);
    } catch { toast.error('mic denied'); }
  };
  const stopRec = () => { if (recRef.current?.state === 'recording') recRef.current.stop(); setRecording(false); };

  const submit = async () => {
    if (!bubbleId) return;
    if (!text.trim() && !audioB64) return;
    setBusy(true);
    try {
      await api.post(`/scenes/${scene.id}/comments`,
        { bubble_id: bubbleId, text: text.trim() || null, voice_b64: audioB64 || null });
      setText(''); setAudioB64(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'could not'); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="scene-comments" className="border-t border-white/10 p-4 sm:p-5">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/60 mb-3">
        <ChatCircleText size={12} weight="duotone" /> comments · {comments.length}
      </div>
      <div className="space-y-2 mb-3 max-h-72 overflow-y-auto pr-1">
        {comments.map((c) => (
          <div key={c.id} data-testid={`sc-comment-${c.id}`} className="rounded-xl bg-white/[0.04] p-3">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-bx-cyan mb-1">
              {c.bubble_name} <span className="text-white/55">· @{c.owner_username}</span><FounderBadge username={c.owner_username} className="ml-0.5" />
            </div>
            {c.text && <div className="text-sm text-white/85">{c.text}</div>}
            {c.voice_url && (
              <audio controls src={cargoMediaSrc({ media_url: c.voice_url })} className="w-full mt-1 h-8" />
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-2">
        <select value={bubbleId} onChange={(e) => setBubbleId(e.target.value)}
          data-testid="sc-cmt-bubble"
          className="bg-white/5 border border-white/10 rounded-full px-2.5 py-1.5 text-[11px] focus:outline-none">
          {bubbles.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
        </select>
        <input value={text}
          data-testid="sc-cmt-text"
          onChange={(e) => setText(e.target.value.slice(0, 240))}
          placeholder="say something back…"
          className="flex-1 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-xs focus:outline-none focus:border-bx-cyan" />
        {!audioB64 && !recording && (
          <button data-testid="sc-cmt-rec" onClick={startRec}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white/60">
            <Microphone size={14} weight="duotone" />
          </button>
        )}
        {recording && (
          <button onClick={stopRec}
            className="p-2 rounded-full bg-red-500/30 text-red-300 animate-pulse">
            <Stop size={14} weight="fill" />
          </button>
        )}
        <button data-testid="sc-cmt-send" onClick={submit} disabled={busy || (!text.trim() && !audioB64)}
          className="p-2 rounded-full btn-volt text-black disabled:opacity-30">
          <PaperPlaneTilt size={14} weight="fill" />
        </button>
      </div>
      {audioB64 && (
        <audio controls src={`data:audio/webm;base64,${audioB64}`} className="w-full h-8" />
      )}
    </div>
  );
};

export default SceneComments;
